import styled from "@emotion/styled"
import { graphql, useStaticQuery } from "gatsby"
import Img from "gatsby-image"
import * as React from "react"
import HeroIllustration from "../../static/illustrations/hero-people.inline.svg"
import Section1MobileBottomLeft from "../../static/illustrations/home/section-1-mobile-bottom-left.inline.svg"
import Section1MobileMiddleRight from "../../static/illustrations/home/section-1-mobile-middle-right.inline.svg"
import Section1MobileTopLeft from "../../static/illustrations/home/section-1-mobile-top-left.inline.svg"
import Section1MobileTopRight from "../../static/illustrations/home/section-1-mobile-top-right.inline.svg"
import Section1TopLeft from "../../static/illustrations/home/section-1-top-left.inline.svg"
import Section1TopMiddle from "../../static/illustrations/home/section-1-top-middle.inline.svg"
import Section1TopRight from "../../static/illustrations/home/section-1-top-right.inline.svg"
import Section2BottomMiddle from "../../static/illustrations/home/section-2-bottom-middle.inline.svg"
import Section2MiddleLeft from "../../static/illustrations/home/section-2-middle-left.inline.svg"
import Section2MobileBottomRight from "../../static/illustrations/home/section-2-mobile-bottom-right.inline.svg"
import Section2MobileMiddleLeft from "../../static/illustrations/home/section-2-mobile-middle-left.inline.svg"
import Section2TopLeft from "../../static/illustrations/home/section-2-top-left.inline.svg"
import Section2TopMiddle from "../../static/illustrations/home/section-2-top-middle.inline.svg"
import Section2TopRight from "../../static/illustrations/home/section-2-top-right.inline.svg"
import Section3BottomLeft from "../../static/illustrations/home/section-3-bottom-left.inline.svg"
import Section3BottomRight from "../../static/illustrations/home/section-3-bottom-right.inline.svg"
import Section3MiddleLeft from "../../static/illustrations/home/section-3-middle-left.inline.svg"
import Section3MobileBottomLeft from "../../static/illustrations/home/section-3-mobile-bottom-left.inline.svg"
import Section3MobileMiddleLeft from "../../static/illustrations/home/section-3-mobile-middle-left.inline.svg"
import Section3TopLeft from "../../static/illustrations/home/section-3-top-left.inline.svg"
import Section3TopMiddle from "../../static/illustrations/home/section-3-top-middle.inline.svg"
import Section3TopRight from "../../static/illustrations/home/section-3-top-right.inline.svg"
import { Footer, Header, ErrorBoundary } from "../components"
import { SEO } from "../components/SEO"
import {
  Box,
  BoxProps,
  Container,
  LinkAnchor,
  Heading,
  ImageFluidContainer,
  Lead,
  List,
  ListItem,
  Text,
  Title,
} from "../elements"
import { Colors } from "../materials"
import { SectionCommunity } from "../partials/SectionCommunity"
import { SubscriptionForm } from "../partials/SubscriptionForm"

const IconContainer: React.FC<BoxProps> = props => (
  <Box
    flex="0 0 auto"
    width={["1.5rem", "2.0rem", "2.5rem"]}
    height={["1.5rem", "2.0rem", "2.5rem"]}
    color={Colors.oc.blue[9]}
    {...props}
  />
)

const Section: React.FC<BoxProps> = ({ css = {}, ...rest }) => (
  <Box as="section" maxWidth={"100vw"} css={{ ...css }} {...rest} />
)

const HeroSvgBase64v =
  "url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyODA0IiBoZWlnaHQ9IjEyMTciIHZpZXdCb3g9IjAgMCAyODA0IDEyMTciPiAgPGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4gICAgPHBvbHlnb24gZmlsbD0iI0U3RjVGRiIgcG9pbnRzPSIwIC41IDAgMTA1MSA0MDMuNSAxMjE2IDI4MDQgODk5LjUgMjgwNCAuNSIvPiAgICA8cG9seWxpbmUgc3Ryb2tlPSIjMTc2NUFCIiBwb2ludHM9Ii41IDEwNTEgNDAzLjUgMTIxNiAyODAwLjUgOTAwIi8+ICA8L2c+PC9zdmc+)"

const Hero = styled(Box)<BoxProps>({
  backgroundColor: "#E7F5FF",
  backgroundPosition: "bottom center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "100%",
  "@media screen and (min-width: 1500px)": {
    backgroundImage: "none",
    backgroundColor: Colors.oc.blue[0],
    borderBottom: `1px solid ${Colors.oc.blue[1]}`,
  },
}).withComponent("section")

// const ImageSection: React.FC<React.HTMLProps<HTMLImageElement>> = ({ src, ...rest }) => (

// )

const Main = styled.main``

const HeroImage = styled(HeroIllustration)<
  React.HTMLAttributes<HTMLImageElement>
>`
  max-width: 350px;
  width: 100%;
  height: auto;
`

export default function IndexPage() {
  const { photo1, photo2, photo3 } = useStaticQuery(graphql`
    fragment sectionImage on File {
      childImageSharp {
        fluid(maxWidth: 320) {
          ...GatsbyImageSharpFluid
        }
      }
    }

    query {
      photo1: file(relativePath: { eq: "photos/home-1.jpg" }) {
        ...sectionImage
      }

      photo2: file(relativePath: { eq: "photos/home-2.jpg" }) {
        ...sectionImage
      }

      photo3: file(relativePath: { eq: "photos/home-3.jpg" }) {
        ...sectionImage
      }
    }
  `)
  const TITLE = "The Accessibility Tool For Your Team | Aditus"
  const DESCRIPTION =
    "Do you know if your website is accessible? Get accessibility reports and learn how to fix your issues. Let's make the web more inclusive."
  return (
    <>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        keywords="accessibility, testing, a11y, audit, compliance, checker, software"
        jsonLd={{
          "@type": "WebPage",
          url: "https://www.aditus.io/",
          name: TITLE,
          description: DESCRIPTION,
        }}
      >
        <meta property="fb:app_id" content="824671391240758" />
        <meta property="og:url" content="https://www.aditus.io" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TITLE} />
        <meta
          property="og:image"
          content="https://www.aditus.io/social/home.png"
        />
        <meta
          property="og:image:alt"
          content="Text reading 'The accessibility tool for your team' with the Aditus logo next to it"
        />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:site_name" content="Aditus" />
        <meta property="og:locale" content="en_US" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@aditus_a11y" />
        <meta name="twitter:url" content="https://www.aditus.io" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta
          name="twitter:image"
          content="https://www.aditus.io/social/home.png"
        />
        <meta
          name="twitter:image:alt"
          content="Text reading 'The accessibility tool for your team' with the aditus logo next to it"
        />
      </SEO>
      <Header />
      <Main css={{ overflowX: "hidden" }}>
        <ErrorBoundary>
          <Hero
            backgroundImage={[
              `${HeroSvgBase64v},
            linear-gradient(
              ${Colors.oc.blue[0]} 0%,
              ${Colors.oc.blue[0]} 80%,
              ${Colors.oc.white} 80%,
              ${Colors.oc.white} 100%
            )`,
              `${HeroSvgBase64v},
            linear-gradient(
              ${Colors.oc.blue[0]} 0%,
              ${Colors.oc.blue[0]} 80%,
              ${Colors.oc.white} 80%,
              ${Colors.oc.white} 100%
            )`,
              `${HeroSvgBase64v},
            linear-gradient(
              ${Colors.oc.blue[0]} 0%,
              ${Colors.oc.blue[0]} 70%,
              ${Colors.oc.white} 70%,
              ${Colors.oc.white} 100%
            )`,
              `${HeroSvgBase64v},
            linear-gradient(
              ${Colors.oc.blue[0]} 0%,
              ${Colors.oc.blue[0]} 65%,
              ${Colors.oc.white} 65%,
              ${Colors.oc.white} 100%
            )`,
            ]}
            pt={[9, 10, 11]}
            pb={[8, 9, 10]}
          >
            <Container
              maxWidth="l"
              css={{ display: "flex", justifyContent: "center" }}
            >
              <Box
                width={[1, 1, "60ch"]}
                flex="0 0 auto"
                color={Colors.tw.gray["700"] as string}
              >
                <Title>
                  The <strong>accessibility</strong> tool
                  <br />
                  for your team
                </Title>

                <Lead mt={2}>Do you know if your website is accessible?</Lead>
                <Lead>
                  Get accessibility reports and learn how to fix your issues.
                  Let&apos;s make the web more inclusive.
                </Lead>

                <Box mt={[6, 6, 8]} />
                <SubscriptionForm>
                  <Box mt={2} />
                  <Text
                    id="email-description"
                    fontSize={1}
                    color={Colors.oc.gray[7]}
                  >
                    Leave your email and we&#39;ll get in touch when we launch.
                    You won&#39;t get SPAMed, promise.
                  </Text>
                </SubscriptionForm>
              </Box>
              <Box ml="6" display={["none", "none", "block"]} />
              <Box
                flex="1 1 auto"
                textAlign="center"
                display={["none", "none", "block"]}
              >
                <HeroImage aria-label="Illustration with 8 people in a circle with the Aditus logo in the middle" />
              </Box>
            </Container>
          </Hero>

          <Section aria-labelledby="section1">
            <Container
              py={[6, 6, 8, 10]}
              display="flex"
              flexWrap={["wrap", "wrap", "nowrap"]}
              justifyContent="center"
            >
              <Box maxWidth="copy" css={{ position: "relative", zIndex: 1 }}>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    top: "-90px",
                    right: "100%",
                    left: "-110px",
                    width: "80px",
                  }}
                >
                  <Section1TopLeft role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["block", "block", "none"]}
                  css={{
                    position: "absolute",
                    top: "-22px",
                    right: "100%",
                    left: "-85px",
                    width: "50px",
                  }}
                >
                  <Section1MobileTopLeft role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["block", "block", "none"]}
                  css={{
                    position: "absolute",
                    top: "-62px",
                    right: "-120px",
                    width: "146px",
                  }}
                >
                  <Section1MobileTopRight role="presentation" />
                </ImageFluidContainer>

                <Heading id="section1">
                  Want an accessible website but don&#39;t know where to start?
                  We can help.
                </Heading>
                <Box mt={5} />
                <List>
                  <ListItem
                    mb={[4, 4, 5]}
                    css={{ display: "flex", alignItems: "center" }}
                  >
                    <IconContainer>
                      {/* TODO: move to file */}
                      <svg
                        viewBox="0 0 24 24"
                        aria-label="Illustration of a man with a bow-tie"
                      >
                        <g
                          strokeLinecap="round"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          fill="none"
                          strokeLinejoin="round"
                        >
                          <path d="M16.773,2.85151c2.63604,2.80201 2.63604,7.34497 0,10.147c-2.63604,2.80201 -6.9099,2.80201 -9.54594,1.77636e-15c-2.63604,-2.80201 -2.63604,-7.34497 -1.77636e-15,-10.147c2.63604,-2.80201 6.9099,-2.80201 9.54594,-8.88178e-16" />
                          <path d="M6.633,3.573l3.31774e-07,3.32188e-07c2.02022,2.02274 4.75822,3.16474 7.617,3.177l-3.75537e-07,1.33621e-10c1.45187,-0.00051657 2.8883,-0.297943 4.221,-0.874" />
                          <path d="M12.274,19.916l-5.2,-2.422l4.96199e-08,2.5058e-08c-0.562007,-0.283813 -1.24768,-0.0582925 -1.53149,0.503715c-0.0704889,0.139582 -0.111508,0.292174 -0.120507,0.448285v3.738l-5.25506e-08,-8.20997e-07c0.0404643,0.632177 0.585748,1.11185 1.21792,1.07139c0.198723,-0.0127198 0.390711,-0.0769562 0.557075,-0.186389Z" />
                          <path d="M12.274,19.916l4.454,-2.422l-3.27313e-08,1.61019e-08c0.487637,-0.239888 1.07741,-0.0390487 1.3173,0.448588c0.0766577,0.155827 0.11071,0.329166 0.0986995,0.502412v3.739l2.88874e-08,3.83605e-07c0.0405599,0.538609 -0.363189,1.00812 -0.901798,1.04868c-0.218922,0.0164859 -0.437023,-0.041167 -0.619202,-0.16368Z" />
                          <path d="M14.121,11.121l1.25571e-07,-1.25534e-07c-1.1715,1.17115 -3.0705,1.17115 -4.242,2.51067e-07" />
                        </g>
                      </svg>
                    </IconContainer>
                    <Text ml={4} fontSize={[2, 3, 3]}>
                      Not all companies have an accessibility expert on their
                      teams. That&#39;s where Aditus comes in.
                    </Text>
                  </ListItem>
                  <ListItem
                    mb={[4, 4, 5]}
                    css={{ display: "flex", alignItems: "center" }}
                  >
                    <IconContainer>
                      {/* TODO: move to file */}
                      <svg
                        viewBox="0 0 24 24"
                        aria-label="Illustration of two people next to a flag"
                      >
                        <g
                          strokeLinecap="round"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          fill="none"
                          strokeLinejoin="round"
                        >
                          <path d="M20.651,23.25l2.24277e-08,6.38621e-08c-0.945194,-2.69141 -3.48445,-4.49457 -6.337,-4.5h-0.006l1.54354e-07,-3.00471e-10c-2.85228,0.00555228 -5.39118,1.80875 -6.336,4.5" />
                          <path d="M10.754,11.031l5.58422e-08,5.78597e-08c1.98104,2.05261 5.02595,2.6643 7.646,1.536" />
                          <path d="M17.2248,10.2082c1.61091,1.61091 1.61091,4.22272 0,5.83363c-1.61091,1.61091 -4.22272,1.61091 -5.83363,0c-1.61091,-1.61091 -1.61091,-4.22272 -1.77636e-15,-5.83363c1.61091,-1.61091 4.22272,-1.61091 5.83363,-1.77636e-15" />
                          <path d="M7.09632,13.6287c1.17157,1.17157 1.17157,3.07107 0,4.24264c-1.17157,1.17157 -3.07107,1.17157 -4.24264,0c-1.17157,-1.17157 -1.17157,-3.07107 0,-4.24264c1.17157,-1.17157 3.07107,-1.17157 4.24264,0" />
                          <path d="M5.864,20.338l1.52313e-07,3.06461e-08c-2.19243,-0.44113 -4.37456,0.80141 -5.114,2.912" />
                          <path d="M14.31,18.75v4.5" />
                          <path d="M23.25,23.25v-22.5h-12.75l3,3l-3,3h12.75" />
                        </g>
                      </svg>
                    </IconContainer>
                    <Text ml={4} fontSize={[2, 3, 3]}>
                      By including accessibility right into your workflow, your
                      team will deliver a more inclusive product that you can be
                      proud of.
                    </Text>
                  </ListItem>
                </List>
              </Box>
              <Box mx={[0, 0, "auto"]} />
              <Box
                mt={[4, 4, 0]}
                ml={[0, 0, 7]}
                width={[1 / 2, 4 / 10, 1 / 3]}
                css={{ position: "relative" }}
              >
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "-53px",
                    right: "100%",
                    left: "-273px",
                    width: "288px",
                  }}
                >
                  <Section2TopMiddle role="presentation" />
                </ImageFluidContainer>

                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    top: "-55px",
                    right: "100%",
                    left: "-60px",
                    width: "80px",
                  }}
                >
                  <Section1TopMiddle role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    top: "40px",
                    right: "-100px",
                    width: "50px",
                  }}
                >
                  <Section1TopRight role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["block", "block", "none"]}
                  css={{
                    position: "absolute",
                    top: "-7px",
                    right: "-88px",
                    width: "66px",
                  }}
                >
                  <Section1MobileMiddleRight role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["block", "block", "none"]}
                  css={{
                    position: "absolute",
                    bottom: "-33px",
                    left: "-77px",
                    width: "37px",
                  }}
                >
                  <Section1MobileBottomLeft role="presentation" />
                </ImageFluidContainer>

                <Img
                  fluid={photo1.childImageSharp.fluid}
                  alt="Man facing a wall in frustration"
                  style={{ position: "relative", borderRadius: "10px" }}
                />
              </Box>
            </Container>
          </Section>
          <Section aria-labelledby="section2">
            <Container
              pt={[6, 6, 8]}
              pb={[6, 6, 8, 10]}
              display="flex"
              flexWrap={["wrap", "wrap", "nowrap"]}
              justifyContent="center"
            >
              <Box
                order={[0, 0, 2]}
                maxWidth="copy"
                css={{ position: "relative" }}
              >
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "100%",
                    right: "-50px",
                    top: "-100px",
                    width: "206px",
                  }}
                >
                  <Section2TopRight role="presentation" />
                </ImageFluidContainer>
                <Heading id="section2">
                  Simple and detailed
                  <br />
                  accessibility testing.
                </Heading>
                <Box mt={5} />
                <List>
                  <ListItem
                    mb={[4, 4, 5]}
                    css={{ display: "flex", alignItems: "center" }}
                  >
                    <IconContainer>
                      {/* TODO: move to file */}
                      <svg
                        viewBox="0 0 24 24"
                        aria-label="Illustration of website with a warning message"
                      >
                        <g
                          strokeLinecap="round"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          fill="none"
                          strokeLinejoin="round"
                        >
                          <path d="M21.76,10.5v-8.247v0c0,-0.828427 -0.671573,-1.5 -1.5,-1.5h-18h-6.55671e-08c-0.828427,3.62117e-08 -1.5,0.671573 -1.5,1.5c0,0 0,0 0,0v16.5l1.68754e-14,-2.26494e-07c-1.25089e-07,0.828427 0.671573,1.5 1.5,1.5h6" />
                          <path d="M0.76,5.25h21" />
                          <path d="M17.256,20.253h-1.63918e-08c0.207107,-9.05293e-09 0.375,0.167893 0.375,0.375c9.05293e-09,0.207107 -0.167893,0.375 -0.375,0.375c-0.207107,9.05293e-09 -0.375,-0.167893 -0.375,-0.375l3.55271e-15,5.66234e-08c-3.12723e-08,-0.207107 0.167893,-0.375 0.375,-0.375" />
                          <path d="M17.26,18v-3" />
                          <path d="M23.069,20.686l6.76906e-08,1.35457e-07c0.437962,0.876416 0.082524,1.94193 -0.793892,2.37989c-0.246269,0.123065 -0.517801,0.187124 -0.793108,0.187108h-8.452l7.90491e-08,-3.2383e-11c-0.979201,0.000401158 -1.77333,-0.793073 -1.77373,-1.77227c-0.000113033,-0.275907 0.0641671,-0.548033 0.187726,-0.794726l4.225,-8.452l7.61911e-09,-1.5252e-08c0.437843,-0.876476 1.50331,-1.23206 2.37978,-0.794216c0.343771,0.171731 0.622485,0.450444 0.794216,0.794216Z" />
                        </g>
                      </svg>
                    </IconContainer>
                    <Text ml={4} fontSize={[2, 3, 3]}>
                      Automated reports continuously monitor your issues, show
                      how they impact your clients and how to fix them.
                    </Text>
                  </ListItem>
                  <ListItem
                    mb={[4, 4, 5]}
                    css={{ display: "flex", alignItems: "center" }}
                  >
                    <IconContainer>
                      {/* TODO: move to file */}
                      <svg
                        viewBox="0 0 24 24"
                        aria-label="Illustration of two people talking"
                      >
                        <g
                          strokeLinecap="round"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          fill="none"
                          strokeLinejoin="round"
                        >
                          <path d="M12.749,21h1.5v2.25l3,-2.25h4.5h-6.55671e-08c0.828427,3.62117e-08 1.5,-0.671573 1.5,-1.5v-4.5v0c0,-0.828427 -0.671573,-1.5 -1.5,-1.5h-9l-6.55671e-08,1.77636e-15c-0.828427,3.62117e-08 -1.5,0.671573 -1.5,1.5c0,0 0,1.77636e-15 0,1.77636e-15v4.5l1.59872e-14,1.60927e-07c1.25089e-07,0.828427 0.671573,1.5 1.5,1.5Z" />
                          <path d="M6.82916,15.0188c1.02513,1.02513 1.02513,2.68718 0,3.71231c-1.02513,1.02513 -2.68718,1.02513 -3.71231,0c-1.02513,-1.02513 -1.02513,-2.68718 -4.44089e-16,-3.71231c1.02513,-1.02513 2.68718,-1.02513 3.71231,0" />
                          <path d="M9.2,23.25l7.00292e-08,2.00816e-07c-0.813618,-2.33313 -3.36456,-3.56493 -5.69769,-2.75131c-1.28876,0.449423 -2.30189,1.46255 -2.75131,2.75131" />
                          <path d="M20.8802,2.26884c1.02513,1.02513 1.02513,2.68718 0,3.71231c-1.02513,1.02513 -2.68718,1.02513 -3.71231,0c-1.02513,-1.02513 -1.02513,-2.68718 0,-3.71231c1.02513,-1.02513 2.68718,-1.02513 3.71231,-4.44089e-16" />
                          <path d="M23.249,10.5l7.00292e-08,2.00816e-07c-0.813618,-2.33313 -3.36456,-3.56493 -5.69769,-2.75131c-1.28876,0.449423 -2.30189,1.46255 -2.75131,2.75131" />
                          <path d="M16.5,15.75h3.75" />
                          <path d="M14.25,18h6" />
                          <path d="M11.249,8.25h-1.5v2.25l-3,-2.25h-4.5l-6.55671e-08,-1.77636e-15c-0.828427,-3.62117e-08 -1.5,-0.671573 -1.5,-1.5c0,0 0,-8.88178e-16 0,-8.88178e-16v-4.5l1.59872e-14,2.26494e-07c-1.25089e-07,-0.828427 0.671573,-1.5 1.5,-1.5h9l-6.55671e-08,1.77636e-15c0.828427,-3.62117e-08 1.5,0.671573 1.5,1.5v4.5l1.77636e-15,6.55671e-08c0,0.828427 -0.671573,1.5 -1.5,1.5Z" />
                          <path d="M7.5,3h-3.75" />
                          <path d="M9.75,5.25h-6" />
                        </g>
                      </svg>
                    </IconContainer>
                    <Text ml={4} fontSize={[2, 3, 3]}>
                      <LinkAnchor
                        href="https://accessibility.digital.gov/"
                        target="_blank"
                        rel="nofollow noopener"
                      >
                        Accessibility is a team&#39;s job.
                      </LinkAnchor>{" "}
                      Aditus will simplify the daily work of developers,
                      designers, writers, and managers.
                    </Text>
                  </ListItem>
                </List>
              </Box>
              <Box mx={[0, 0, "auto"]} order={1} />
              <Box
                mt={[4, 4, 0]}
                mr={[0, 0, 7]}
                width={[1 / 2, 4 / 10, 1 / 3]}
                order={[2, 2, 0]}
                css={{ position: "relative" }}
              >
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "100%",
                    top: "-220px",
                    left: "-100px",
                    width: "200px",
                  }}
                >
                  <Section2TopLeft role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "-45px",
                    left: "-115px",
                    width: "60px",
                  }}
                >
                  <Section2MiddleLeft role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "-73px",
                    right: "-68px",
                    width: "120px",
                  }}
                >
                  <Section2BottomMiddle role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["block", "block", "none"]}
                  css={{
                    position: "absolute",
                    top: "-3px",
                    left: "-94px",
                    width: "60px",
                  }}
                >
                  <Section2MobileMiddleLeft role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["block", "block", "none"]}
                  css={{
                    position: "absolute",
                    bottom: "-22px",
                    right: "-68px",
                    width: "42px",
                  }}
                >
                  <Section2MobileBottomRight role="presentation" />
                </ImageFluidContainer>

                <Img
                  fluid={photo2.childImageSharp.fluid}
                  alt="Close-up photo of hand tools"
                  style={{ borderRadius: "10px" }}
                />
              </Box>
            </Container>
          </Section>
          <Section aria-labelledby="section3">
            <Container
              pt={[6, 6, 8]}
              pb={[8, 9, 9, 9]}
              display="flex"
              flexWrap={["wrap", "wrap", "nowrap"]}
              justifyContent="center"
            >
              <Box maxWidth="copy" css={{ position: "relative" }}>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "100%",
                    top: "-124px",
                    left: "-16px",
                    width: "100px",
                  }}
                >
                  <Section3TopLeft role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "100%",
                    top: "-88px",
                    right: "-82px",
                    width: "185px",
                  }}
                >
                  <Section3TopMiddle role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    top: "13px",
                    right: "100%",
                    left: "-180px",
                    width: "156px",
                  }}
                >
                  <Section3MiddleLeft role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "-155px",
                    left: "-156px",
                    width: "500px",
                  }}
                >
                  <Section3BottomLeft role="presentation" />
                </ImageFluidContainer>
                <Heading id="section3">Expand your market reach.</Heading>
                <Box mt={5} />
                <List>
                  <ListItem
                    mb={[4, 4, 5]}
                    css={{ display: "flex", alignItems: "center" }}
                  >
                    <IconContainer>
                      {/* TODO: move to file */}
                      <svg
                        viewBox="0 0 24 24"
                        aria-label="Illustration of a person in a wheelchair"
                      >
                        <g
                          strokeLinecap="round"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          fill="none"
                          strokeLinejoin="round"
                        >
                          <path d="M12,0.8l-3.93403e-07,1.06581e-14c4.97056,-2.1727e-07 9,4.02944 9,9c0,7.9 -6.932,12.331 -8.629,13.3l3.45785e-08,-1.96818e-08c-0.230319,0.131096 -0.512681,0.131096 -0.743,3.93637e-08c-1.697,-0.968 -8.628,-5.4 -8.628,-13.3l1.03029e-13,1.35896e-06c-7.50534e-07,-4.97056 4.02944,-9 9,-9Z" />
                          <path d="M10.875,8.302l0.375,3.75h3.75l0.75,3.75" />
                          <path d="M12.2008,5.10117c0.732233,0.732233 0.732233,1.91942 0,2.65165c-0.732233,0.732233 -1.91942,0.732233 -2.65165,0c-0.732233,-0.732233 -0.732233,-1.91942 0,-2.65165c0.732233,-0.732233 1.91942,-0.732233 2.65165,0" />
                          <path d="M13.622,12.052l3.99906e-08,8.46937e-08c0.619005,1.31095 0.0580686,2.8755 -1.25289,3.4945c-1.31095,0.619005 -2.8755,0.0580686 -3.4945,-1.25289c-0.619005,-1.31095 -0.0580686,-2.8755 1.25289,-3.4945c0.305261,-0.144138 0.635459,-0.228042 0.9725,-0.247115" />
                        </g>
                      </svg>
                    </IconContainer>
                    <Text ml={4} fontSize={[2, 3, 3]}>
                      Don&#39;t exclude any prospective client. There are more
                      than one billion{" "}
                      <LinkAnchor
                        href="https://www.w3.org/WAI/people-use-web/"
                        target="_blank"
                        rel="nofollow noopener"
                      >
                        people with disabilities
                      </LinkAnchor>{" "}
                      in the world.
                    </Text>
                  </ListItem>
                  <ListItem
                    mb={[4, 4, 5]}
                    css={{ display: "flex", alignItems: "center" }}
                  >
                    <IconContainer>
                      {/* TODO: move to file */}
                      <svg
                        viewBox="0 0 24 24"
                        aria-label="Illustration of an approval stamp"
                      >
                        <g
                          strokeLinecap="round"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          fill="none"
                          strokeLinejoin="round"
                        >
                          <path d="M21.684,9.39l1.93103e-09,-7.08496e-09c-0.0920181,0.337614 -0.279108,0.641678 -0.539,0.876l-0.972,0.878l-7.98472e-08,7.20684e-08c-0.74945,0.676438 -0.808638,1.83235 -0.1322,2.5818c0.0417829,0.0462928 0.0859073,0.0904171 0.1322,0.1322l0.972,0.878l1.5053e-08,1.35764e-08c0.749702,0.67616 0.809319,1.83205 0.133159,2.58175c-0.368791,0.408902 -0.902256,0.629777 -1.45216,0.601249l-1.308,-0.067l2.09713e-10,1.0612e-11c-1.00829,-0.0510225 -1.86703,0.724993 -1.91805,1.73328c-0.0031307,0.0618676 -0.00311455,0.123854 4.83961e-05,0.18572l0.067,1.308l-4.12925e-08,-7.69061e-07c0.0541285,1.00812 -0.719238,1.86925 -1.72736,1.92338c-0.551622,0.0296178 -1.08705,-0.191804 -1.45664,-0.602378l-0.878,-0.972l-1.85307e-08,-2.05308e-08c-0.676438,-0.74945 -1.83235,-0.808638 -2.5818,-0.1322c-0.0462928,0.0417829 -0.0904171,0.0859073 -0.1322,0.1322l-0.878,0.972l-2.04801e-08,2.27118e-08c-0.676091,0.749763 -1.83198,0.809486 -2.58174,0.133395c-0.409533,-0.369292 -0.630472,-0.903723 -0.601262,-1.4544l0.07,-1.308l-4.86391e-08,9.50835e-07c0.0515767,-1.00826 -0.723967,-1.86742 -1.73223,-1.919c-0.0622173,-0.00318268 -0.124557,-0.00318272 -0.186774,-9.72776e-08l-1.308,0.067l9.44433e-09,-4.89944e-10c-1.00822,0.0523037 -1.86795,-0.72262 -1.92025,-1.73084c-0.0285274,-0.549903 0.192347,-1.08337 0.601249,-1.45216l0.972,-0.878l4.2098e-08,-3.79968e-08c0.74945,-0.676438 0.808638,-1.83235 0.1322,-2.5818c-0.0417829,-0.0462928 -0.0859073,-0.0904171 -0.1322,-0.1322l-0.972,-0.878l-4.19141e-08,-3.78127e-08c-0.749612,-0.676259 -0.809076,-1.83216 -0.132817,-2.58177c0.368041,-0.407961 0.900063,-0.628741 1.44882,-0.601232l1.308,0.067l4.75589e-08,2.51139e-09c1.00817,0.0532374 1.86861,-0.72089 1.92185,-1.72906c0.00334089,-0.0632674 0.0033904,-0.126666 0.000148336,-0.189938l-0.07,-1.308l-3.33303e-08,-6.14817e-07c-0.0546505,-1.0081 0.71827,-1.86962 1.72637,-1.92427c0.551442,-0.0298946 1.08684,0.191109 1.45663,0.601274l0.878,0.972l1.34124e-07,1.48964e-07c0.674789,0.74945 1.82936,0.809975 2.57881,0.135187c0.0473921,-0.0426708 0.0925162,-0.0877949 0.135187,-0.135187l0.878,-0.972l-1.79091e-07,1.97444e-07c0.519496,-0.572733 1.34303,-0.756897 2.057,-0.460001" />
                          <path d="M7.6,10.641l2.513,3.351l-5.64451e-08,-7.5216e-08c0.497243,0.662602 1.43748,0.796651 2.10008,0.299409c0.0908798,-0.0681999 0.173716,-0.146505 0.246916,-0.233408l10.788,-12.807" />
                        </g>
                      </svg>
                    </IconContainer>
                    <Text ml={4} fontSize={[2, 3, 3]}>
                      Minimize legal risk and be compliant with WCAG 2.1,
                      Section 508, and ADA.
                    </Text>
                  </ListItem>
                </List>
              </Box>
              <Box mx={[0, 0, "auto"]} />
              <Box
                mt={[4, 4, 0]}
                ml={[0, 0, 7]}
                width={[1 / 2, 4 / 10, 1 / 3]}
                css={{ position: "relative" }}
              >
                <Img
                  fluid={photo3.childImageSharp.fluid}
                  alt="Notebook with hand-drawn chart of a product getting better over time."
                  style={{ borderRadius: "10px" }}
                />
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "100%",
                    top: "-85px",
                    right: "-72px",
                    width: "72px",
                  }}
                >
                  <Section3TopRight role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["none", "none", "block", "block"]}
                  css={{
                    position: "absolute",
                    bottom: "-54px",
                    right: "-128px",
                    width: "168px",
                  }}
                >
                  <Section3BottomRight role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["block", "block", "none"]}
                  css={{
                    position: "absolute",
                    top: "-11px",
                    left: "-111px",
                    width: "57px",
                  }}
                >
                  <Section3MobileMiddleLeft role="presentation" />
                </ImageFluidContainer>
                <ImageFluidContainer
                  display={["block", "block", "none"]}
                  css={{
                    position: "absolute",
                    bottom: "-114px",
                    left: "-141px",
                    width: "128px",
                  }}
                >
                  <Section3MobileBottomLeft role="presentation" />
                </ImageFluidContainer>
              </Box>
            </Container>
          </Section>

          <SectionCommunity withHiddenDescription={false} />
        </ErrorBoundary>
      </Main>
      <Footer />
    </>
  )
}
